import type { StaffDetail } from "@/api/guide";

interface LightboxProps {
  guide: StaffDetail;
  /** 当前预览索引，null 表示关闭 */
  index: number | null;
  /** 切换索引回调 */
  onIndexChange: (idx: number | null) => void;
}

export default function Lightbox({ guide, index, onIndexChange }: LightboxProps) {
  if (index === null) return null;

  const total = guide.images?.length ?? 0;

  const goPrev = () => {
    if (index > 0) onIndexChange(index - 1);
  };

  const goNext = () => {
    if (index < total - 1) onIndexChange(index + 1);
  };

  const handleClose = () => onIndexChange(null);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={handleClose}
    >
      {/* 关闭按钮 */}
      <button
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        className="absolute top-12 right-4 w-10 h-10 flex items-center justify-center rounded-full z-[110] cursor-pointer"
        style={{ background: "rgba(255,255,255,0.15)" }}
      >
        <i className="ri-close-line text-white" style={{ fontSize: 24 }} />
      </button>

      {/* 左箭头 */}
      {index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full z-[110] cursor-pointer active:scale-90 transition-transform"
          style={{ background: "rgba(255,255,255,0.15)" }}
        >
          <i className="ri-arrow-left-s-line text-white" style={{ fontSize: 26 }} />
        </button>
      )}

      {/* 右箭头 */}
      {index < total - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full z-[110] cursor-pointer active:scale-90 transition-transform"
          style={{ background: "rgba(255,255,255,0.15)" }}
        >
          <i className="ri-arrow-right-s-line text-white" style={{ fontSize: 26 }} />
        </button>
      )}

      {/* 当前图片 — 支持左右滑动切换 */}
      <div
        className="relative w-full h-full overflow-hidden"
        onTouchStart={(e) => {
          (e.currentTarget as HTMLElement).dataset.startX = String(e.touches[0].clientX);
        }}
        onTouchEnd={(e) => {
          const el = e.currentTarget as HTMLElement;
          const startX = Number(el.dataset.startX || 0);
          const endX = e.changedTouches[0].clientX;
          const diff = endX - startX;

          // 滑动超过 50px 触发切换
          if (Math.abs(diff) > 50) {
            e.preventDefault();
            if (diff > 0) {
              goPrev(); // 右滑 → 上一张
            } else {
              goNext(); // 左滑 → 下一张
            }
          }
        }}
      >
        <img
          key={index} // 索引变化时强制重新渲染，避免复用旧图位置
          src={guide.images[index]}
          alt={`相册${index + 1}`}
          className="absolute inset-0 m-auto max-w-[90vw] max-h-[80vh] object-contain select-none pointer-events-none transition-opacity duration-150"
          draggable={false}
        />
        <span className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 text-sm">
          {index + 1} / {total}
        </span>
      </div>
    </div>
  );
}

/** 相册网格 + 点击打开 lightbox */
export function PhotoGallery({ guide, onOpen }: { guide: StaffDetail; onOpen: (idx: number) => void }) {
  if (guide.images.length <= 1) return null;
  return (
    <div className="mx-4 mt-4 p-4 rounded-2xl" style={{ background: "rgba(255,255,255,0.05)" }}>
      <h2 className="text-sm font-semibold text-white mb-3">相册展示</h2>
      <div className="grid grid-cols-3 gap-2">
        {guide.images.map((imgUrl, idx) => (
          <img key={idx} src={imgUrl} alt={`相册${idx + 1}`}
            className="w-full aspect-square object-cover rounded-xl cursor-pointer transition-transform active:scale-95"
            onClick={() => onOpen(idx)}
          />
        ))}
      </div>
    </div>
  );
}
